import Video2Icon from "../Icons/Video2Icon";
import PdfIcon from "../Icons/PdfIcon";
import QuestionMarkIcon from "../Icons/QuestionMarkIcon";
import LockIcon from "../Icons/LockIcon";
import { LectureByIdDTO } from "../../api/Lecture/GetLecturerById";
import { Link } from "react-router-dom";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../Shared/accordion";
import GreenCheckIcon from "../Icons/GreenCheckIcon";
import { formatDuration } from "../../hooks/DurationConverter";

const ContentAccordion = ({
  lectureName,
  onClick,
  data,
  onVideoSelect,
  activeVideoId,
  itemId,
  onQuizSelect,
  onEssaySelect,
  activeQuizUrl,
  activeEssayUrl,
  onExamSelect,
  activeExamUrl,
  onIfrsSelect,
  activeIfrsUrl,
  cbqExams = [],
  onCbqSelect,
  activeCbqUrl,
  lectureTime,
  canAccess = true,
  demoEnabled = false,
  lectureLocked = false,
}: {
  lectureName?: string;
  data?: LectureByIdDTO;
  onClick?: () => void;
  isActive?: boolean;
  onVideoSelect: (
    videoUrl: string,
    videoId: string,
    otp: string,
    playback: string,
    name: string
  ) => void;
  activeVideoId: string;
  itemId: string;
  onQuizSelect: (quizId: string, quizTitle: string) => void;
  onEssaySelect: (quizId: string, quizTitle: string) => void;
  activeQuizUrl: string;
  activeEssayUrl: string;
  onExamSelect: (quizId: string, quizTitle: string, mcqId: string) => void;
  activeExamUrl: string;
  onIfrsSelect?: (ifrsId: string, ifrsTitle: string) => void;
  activeIfrsUrl?: string;
  // CBQ exams attached to this lecture (fetched separately from the lecture
  // payload). Always non-demoable: locked unless the user owns the course.
  cbqExams?: { id: string; title: string }[];
  onCbqSelect?: (cbqExamId: string, cbqTitle: string) => void;
  activeCbqUrl?: string;
  lectureTime?: string;
  // true = user owns course (full access).
  canAccess?: boolean;
  // true = logged-in user may open isDemo items even without owning the course.
  demoEnabled?: boolean;
  // true = this whole lecture is locked (not owned and not a demo lecture).
  lectureLocked?: boolean;
}) => {
  const quizizz = data?.quizizz ?? [];
  const quizEssay = data?.QuizEssay ?? [];
  const resources = data?.resources ?? [];
  const videos = data?.videos ?? [];
  const exam = data?.Exam ?? [];
  const ifrs = data?.Ifrs ?? [];

  const unlocked = (item: { isDemo?: boolean }) =>
    canAccess || (demoEnabled && !!item?.isDemo);

  const ifrsWithVideo = ifrs.filter((ifrsItem) => ifrsItem.videoId);
  const ifrsWithoutVideo = ifrs.filter((ifrsItem) => !ifrsItem.videoId);

  const lockedRow = "opacity-60 cursor-not-allowed select-none";
  const lockTitle = "Purchase the course to unlock";

  return (
    <AccordionItem id="lecture" value={itemId}>
      <AccordionTrigger
        className={`accordionTrigger relative text-sm sm:text-base font-semibold flex justify-between gap-2 ${
          lectureLocked ? lockedRow : ""
        }`}
        onClick={lectureLocked ? undefined : onClick}
        title={lectureLocked ? lockTitle : undefined}
      >
        <span className="text-start flex items-center gap-2">
          {lectureLocked && <LockIcon className="size-4 shrink-0" />}
          {lectureName}
        </span>
        <span className="text-sm mr-5">{lectureTime}</span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="space-y-4">
          {videos.map((video) => {
            const open = unlocked(video);
            return (
              <div key={video.id}>
                <div
                  onClick={
                    open
                      ? () =>
                          onVideoSelect(
                            video.url,
                            video.id,
                            video.cipher.otp,
                            video.cipher.playbackInfo,
                            video.name
                          )
                      : undefined
                  }
                  title={open ? undefined : lockTitle}
                  className={`flex gap-2 border-l-[3.6px] py-2 pr-4 pl-3 transition-all duration-300 ease-in-out ${open ? "cursor-pointer" : lockedRow} ${activeVideoId === video.id ? "bg-[#EFF9FF] border-primary" : "border-transparent"}`}
                >
                  <div
                    className={`${activeVideoId === video.id ? "text-primary" : "border-transparent"}`}
                  >
                    {!open ? (
                      <LockIcon className="size-5" />
                    ) : video.watched ? (
                      <GreenCheckIcon />
                    ) : (
                      <Video2Icon />
                    )}
                  </div>
                  <div className="flex justify-between w-full">
                    <p className="text-lightGray">
                      <span
                        className={`${video.watched ? "!text-green-500" : ""} font-semibold text-sm sm:text-lg ${activeVideoId === video.id ? "text-primary" : "text-lightGray"}`}
                      >
                        Video :
                      </span>
                      {video.name}
                    </p>
                    <p className="text-sm">
                      {formatDuration(Number(video.duration))}
                    </p>
                  </div>
                </div>

                {ifrsWithVideo
                  .filter((ifrsItem) => ifrsItem.videoId === video.id)
                  .map((ifrsItem) => {
                    const iopen = unlocked(ifrsItem);
                    return (
                      <div
                        key={ifrsItem.id}
                        onClick={
                          iopen
                            ? () => onIfrsSelect?.(ifrsItem.id, ifrsItem.title)
                            : undefined
                        }
                        title={iopen ? undefined : lockTitle}
                        className={`flex gap-2 items-center border-l-[3.6px] py-2 pr-4 pl-3 ${iopen ? "cursor-pointer" : lockedRow} ${activeIfrsUrl === ifrsItem.id ? "bg-[#dbeafe] border-primary" : "bg-[#fefce8] border-amber-300"}`}
                      >
                        {iopen ? (
                          <QuestionMarkIcon />
                        ) : (
                          <LockIcon className="size-5" />
                        )}
                        <div className="text-lightGray font-bold">
                          {ifrsItem.title}
                        </div>
                      </div>
                    );
                  })}
              </div>
            );
          })}

          {resources.map((resource) => {
            const open = unlocked(resource);
            return open ? (
              <div
                key={resource.id}
                className="flex gap-2 items-center border-l-[3.6px] border-transparent py-2 pr-4 pl-3 cursor-pointer"
              >
                <PdfIcon />
                <Link target="_blank" to={resource.url} className="text-lightGray">
                  <span className="font-semibold text-sm sm:text-lg text-bruntBlack">
                    PDF :
                  </span>
                  {resource.title}
                </Link>
              </div>
            ) : (
              <div
                key={resource.id}
                title={lockTitle}
                className={`flex gap-2 items-center border-l-[3.6px] border-transparent py-2 pr-4 pl-3 ${lockedRow}`}
              >
                <LockIcon className="size-5" />
                <div className="text-lightGray">
                  <span className="font-semibold text-sm sm:text-lg text-bruntBlack">
                    PDF :
                  </span>
                  {resource.title}
                </div>
              </div>
            );
          })}

          {quizizz.map((quiz) => {
            const open = unlocked(quiz);
            return (
              <div
                key={quiz.id}
                onClick={open ? () => onQuizSelect(quiz.id, quiz.title) : undefined}
                title={open ? undefined : lockTitle}
                className={`flex gap-2 items-center border-l-[3.6px] py-2 pr-4 pl-3 ${open ? "cursor-pointer" : lockedRow} ${activeQuizUrl === quiz.id ? "bg-[#EFF9FF] border-primary" : "border-transparent"}`}
              >
                {open ? <QuestionMarkIcon /> : <LockIcon className="size-5" />}
                <div className="text-lightGray">
                  <span className="font-semibold text-sm sm:text-lg text-bruntBlack">
                    Quiz :
                  </span>
                  {quiz.title}
                </div>
              </div>
            );
          })}

          {quizEssay.map((quiz) => {
            const open = unlocked(quiz);
            return (
              <div
                key={quiz.id}
                onClick={open ? () => onEssaySelect(quiz.id, quiz.title) : undefined}
                title={open ? undefined : lockTitle}
                className={`flex gap-2 items-center border-l-[3.6px] py-2 pr-4 pl-3 ${open ? "cursor-pointer" : lockedRow} ${activeEssayUrl === quiz.id ? "bg-[#EFF9FF] border-primary" : "border-transparent"}`}
              >
                {open ? <QuestionMarkIcon /> : <LockIcon className="size-5" />}
                <div className="text-lightGray">
                  <span className="font-semibold text-sm sm:text-lg text-bruntBlack">
                    Essay Quiz :
                  </span>
                  {quiz.title}
                </div>
              </div>
            );
          })}

          {exam.map((quiz) => {
            const open = unlocked(quiz);
            return (
              <div
                key={quiz.id}
                onClick={
                  open
                    ? () => onExamSelect(quiz.id, quiz.title, quiz.quizId)
                    : undefined
                }
                title={open ? undefined : lockTitle}
                className={`flex gap-2 items-center border-l-[3.6px] py-2 pr-4 pl-3 ${open ? "cursor-pointer" : lockedRow} ${activeExamUrl === quiz.id ? "bg-[#EFF9FF] border-primary" : "border-transparent"}`}
              >
                {open ? <QuestionMarkIcon /> : <LockIcon className="size-5" />}
                <div className="text-lightGray">
                  <span className="font-semibold text-sm sm:text-lg text-bruntBlack">
                    Mixed Exam :
                  </span>
                  {quiz.title}
                </div>
              </div>
            );
          })}

          {cbqExams.map((cbq) => {
            // CBQ exams are never demoable; they unlock only with full access.
            const open = canAccess;
            return (
              <div
                key={cbq.id}
                onClick={open ? () => onCbqSelect?.(cbq.id, cbq.title) : undefined}
                title={open ? undefined : lockTitle}
                className={`flex gap-2 items-center border-l-[3.6px] py-2 pr-4 pl-3 ${open ? "cursor-pointer" : lockedRow} ${activeCbqUrl === cbq.id ? "bg-[#EFF9FF] border-primary" : "border-transparent"}`}
              >
                {open ? <QuestionMarkIcon /> : <LockIcon className="size-5" />}
                <div className="text-lightGray">
                  <span className="font-semibold text-sm sm:text-lg text-bruntBlack">
                    CBQ Exam :
                  </span>
                  {cbq.title}
                </div>
              </div>
            );
          })}

          {ifrsWithoutVideo.map((ifrsItem) => {
            const open = unlocked(ifrsItem);
            return (
              <div
                key={ifrsItem.id}
                onClick={
                  open ? () => onIfrsSelect?.(ifrsItem.id, ifrsItem.title) : undefined
                }
                title={open ? undefined : lockTitle}
                className={`flex gap-2 items-center border-l-[3.6px] py-2 pr-4 pl-3 ${open ? "cursor-pointer" : lockedRow} ${activeIfrsUrl === ifrsItem.id ? "bg-[#EFF9FF] border-primary" : "border-transparent"}`}
              >
                {open ? <QuestionMarkIcon /> : <LockIcon className="size-5" />}
                <div className="text-lightGray font-bold">{ifrsItem.title}</div>
              </div>
            );
          })}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export default ContentAccordion;
